/**
 * Pixi Application Setup
 *
 * Comprehensive initialization of Pixi.js app with all containers and resources.
 * Returns everything needed for the visualization.
 */

import { Application, Container, Graphics, Sprite, Texture } from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import { SCENE_WIDTH, SCENE_HEIGHT, BLOCK_COLOR, MAX_ACTIVE_TXS, TX_SIZE } from '../config/visualization.config';
import { ThemeManager } from '../rendering/ThemeManager';

export interface PixiContainers {
  blockchain: Container;
  miniblock: Container;
  tx: Container;
  circles: Container;
  lines: Container;
  sequencer: Container;
  barGraph: Container;
  stats: Container;
}

export interface PixiAppInstance {
  app: Application;
  viewport: Viewport;
  containers: PixiContainers;
  sprites: {
    txSprites: Sprite[];
    txTexture: Texture;
  };
  viewportSize: {
    width: number;
    height: number;
  };
  worldSize: {
    width: number;
    height: number;
  };
  cleanup: () => void;
  resize: () => void;
  updateScale: () => void;
  onResizeComplete: (callback: (worldWidth: number, worldHeight: number) => void) => void;
  createSpritePool: () => void;
}

/**
 * Initialize Pixi Application with all containers and resources
 */
export async function initPixiApp(
  containerElement: HTMLElement,
  updateCallback: (deltaTime: number) => void
): Promise<PixiAppInstance> {
  const app = new Application();

  // Get container dimensions
  const viewportWidth = containerElement.clientWidth;
  const viewportHeight = containerElement.clientHeight;

  // console.log(`📏 Container dimensions: ${viewportWidth}x${viewportHeight}`);

  // Initialize Pixi app with quality settings (performance optimized via sprite scaling instead of Graphics redraws)
  await app.init({
    width: viewportWidth,
    height: viewportHeight,
    backgroundColor: ThemeManager.colors.bgPrimary,
    preference: 'webgl',
    antialias: true, // Enabled for visual quality - performance optimized via sprite scaling (not Graphics redraws)
    resolution: window.devicePixelRatio || 1, // Match display resolution for crisp rendering on 4K displays
    powerPreference: 'high-performance',
    eventMode: 'passive', // Enable events for circle clicks (passive for minimal performance impact)
    eventFeatures: {
      globalMove: false, // Disable expensive global pointer tracking - prevents FPS drops on mouse enter
    },
  });

  // Subscribe to theme changes to update canvas background
  const unsubscribeTheme = ThemeManager.subscribe((colors) => {
    app.renderer.background.color = colors.bgPrimary;
  });

  containerElement.appendChild(app.canvas as HTMLCanvasElement);

  // Set CSS to make canvas fit viewport perfectly (handles high DPI scaling)
  const canvas = app.canvas as HTMLCanvasElement;
  canvas.style.width = `${viewportWidth}px`;
  canvas.style.height = `${viewportHeight}px`;
  canvas.style.display = 'block';

  // ============================================================================
  // CREATE VIEWPORT (Camera/Scrolling)
  // ============================================================================

  // Calculate world size
  // World width is SCENE (1920px), height adjusted for viewport scaling
  const worldWidth = SCENE_WIDTH;  // 1920px - actual content width
  const scale = viewportWidth / SCENE_WIDTH; // Scale factor from fitWidth()

  // Minimum circle spawn area (in visual pixels on screen)
  const CIRCLE_START_Y = 400; // Approximate Y where circles start (SEQUENCER_BOTTOM + padding + MAX_RADIUS)
  const MIN_CIRCLE_AREA_HEIGHT = 600; // Minimum visual height for circle area
  const minCircleAreaWorld = MIN_CIRCLE_AREA_HEIGHT / scale; // Convert to world coordinates

  // Circle MAX_Y should be either viewport bottom (tall screens) or minimum area (short screens)
  const viewportHeightWorld = viewportHeight / scale;
  const minMaxY = CIRCLE_START_Y + minCircleAreaWorld;
  const maxY = Math.max(viewportHeightWorld, minMaxY);

  // World height is exactly where circles end (no extra space)
  const worldHeight = maxY;

  // console.log(`🌍 World size set to: ${worldWidth}x${worldHeight.toFixed(0)} (scale: ${scale.toFixed(3)}, maxY: ${maxY.toFixed(0)})`);

  // Create viewport with drag/scroll capabilities
  const viewport = new Viewport({
    screenWidth: viewportWidth,
    screenHeight: viewportHeight,
    worldWidth: worldWidth,     // 1920px
    worldHeight: worldHeight,   // Scrollable area
    events: app.renderer.events,
    passiveWheel: false,        // Allow preventDefault on wheel events
    stopPropagation: true,      // Stop events from bubbling up
  });

  // Add viewport to stage (viewport is the root container now)
  app.stage.addChild(viewport);

  // Scale scene to fit screen width (e.g., 1440px screen / 1920px scene = 0.75x scale)
  viewport.fitWidth(SCENE_WIDTH, false, true, false);

  // Check if scrolling is needed (world taller than viewport = short screen)
  // Add threshold of 20px to avoid enabling scroll for tiny differences (rounding, retina, etc)
  const SCROLL_ENABLE_THRESHOLD = 20;
  const needsScroll = worldHeight > viewportHeightWorld + SCROLL_ENABLE_THRESHOLD;

  // NOTE: Drag scrolling disabled - use wheel only for vertical scroll
  // This prevents accidental canvas dragging when interacting with circles
  if (needsScroll) {
    // console.log('📜 Viewport scroll available via wheel (drag disabled)');
  } else {
    // console.log('📜 Viewport scroll not needed (screen tall enough for content)');
  }

  // Always clamp horizontal and zoom (prevents accidental drift)
  viewport
    .clamp({
      direction: 'x', // Lock horizontal axis to prevent drift
      underflow: 'center', // Center content horizontally if smaller than viewport
    })
    .clampZoom({
      minScale: viewport.scale.x, // Lock to fitted scale (e.g., 0.75x)
      maxScale: viewport.scale.x, // No zoom, only vertical scroll
    });

  // console.log(`🎥 Viewport created: screen ${viewportWidth}x${viewportHeight}, world ${worldWidth}x${worldHeight}`);

  // ============================================================================
  // NATIVE SCROLL HANDLING (like txstreet/Phaser camera.scrollY)
  // ============================================================================

  // Scroll state (target and current for smooth interpolation)
  let targetScrollY = 0;
  let currentScrollY = 0;
  // Max scroll in world coordinates (viewport shows viewportHeight/scale world pixels)
  // Only allow scroll if we exceed the threshold, otherwise lock to 0
  let maxScrollY = needsScroll ? Math.max(0, worldHeight - viewportHeightWorld) : 0;

  // Listen to native wheel events (better performance than pixi-viewport)
  const handleWheel = (e: WheelEvent) => {
    e.preventDefault();

    // Use native deltaY (respects OS scroll acceleration)
    targetScrollY += e.deltaY;

    // Clamp to world bounds
    targetScrollY = Math.max(0, Math.min(targetScrollY, maxScrollY));
  };
  canvas.addEventListener('wheel', handleWheel, { passive: false });

  // Prevent touch events from scrolling the page
  const handleTouchStart = (e: TouchEvent) => {
    e.preventDefault();
  };
  canvas.addEventListener('touchstart', handleTouchStart, { passive: false });

  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault();
  };
  canvas.addEventListener('touchmove', handleTouchMove, { passive: false });

  // Update max scroll when world height changes (called during resize)
  const updateMaxScroll = () => {
    const screenHeight = viewport.screenHeight;
    const worldHeight = viewport.worldHeight;
    const currentScale = viewport.scale.x;
    // Calculate how much of the world the viewport shows (in world coordinates)
    const visibleWorldHeight = screenHeight / currentScale;
    // Only enable scroll if world exceeds viewport by more than threshold
    const scrollDifference = worldHeight - visibleWorldHeight;
    maxScrollY = scrollDifference > SCROLL_ENABLE_THRESHOLD ? Math.max(0, scrollDifference) : 0;

    // Clamp current scroll if it's now out of bounds
    if (targetScrollY > maxScrollY) {
      targetScrollY = maxScrollY;
    }
    if (currentScrollY > maxScrollY) {
      currentScrollY = maxScrollY;
    }
  };

  // ============================================================================
  // CREATE ALL CONTAINERS (added to viewport instead of stage)
  // ============================================================================

  // Blockchain container (for blocks)
  const blockchainContainer = new Container();
  blockchainContainer.y = 0; // Position from top of canvas
  blockchainContainer.eventMode = 'none'; // Disable events for performance
  viewport.addChild(blockchainContainer);

  // Lines container (behind blocks)
  const linesContainer = new Container();
  linesContainer.eventMode = 'none'; // Disable events for performance
  blockchainContainer.addChild(linesContainer);

  // Miniblock container (on top of blockchain)
  const miniblockContainer = new Container();
  miniblockContainer.y = 0; // Match blockchain Y offset
  miniblockContainer.eventMode = 'none'; // Disable events for performance
  viewport.addChild(miniblockContainer);

  // Sequencer container (always interactive for clicks)
  const sequencerContainer = new Container();
  sequencerContainer.y = 0;
  sequencerContainer.eventMode = 'static'; // Enable events for sequencer clicks
  viewport.addChild(sequencerContainer);

  // Transaction sprites container (render below circles)
  const txContainer = new Container();
  txContainer.y = 0; // Match other containers
  txContainer.eventMode = 'none'; // Disable events for performance
  viewport.addChild(txContainer);

  // Circles container (render ABOVE transaction particles)
  const circlesContainer = new Container();
  circlesContainer.y = 0;
  circlesContainer.eventMode = 'passive'; // Enable events for circle clicks
  viewport.addChild(circlesContainer); // Add after txContainer to render on top

  // Bar graph container (above sequencer) - interaction enabled only when paused
  const barGraphContainer = new Container();
  barGraphContainer.y = 0; // Match other containers
  barGraphContainer.eventMode = 'none'; // Disabled by default, enabled when paused
  viewport.addChild(barGraphContainer);

  // Stats container (logo and stats panel)
  const statsContainer = new Container();
  statsContainer.y = 0; // Match other containers
  statsContainer.eventMode = 'passive'; // Allow events to pass through to children (for More Stats button)
  viewport.addChild(statsContainer);

  // ============================================================================
  // SPRITE POOL FOR TRANSACTIONS (DEFERRED)
  // ============================================================================

  // Create white texture for sprites (size controlled by TX_SIZE config)
  // Using white base so we can tint to any color for theme support
  const graphics = new Graphics();
  graphics.rect(0, 0, TX_SIZE, TX_SIZE);
  graphics.fill(0xffffff);
  const txSpriteTexture = app.renderer.generateTexture(graphics);

  // Sprite pool array
  const txSprites: Sprite[] = [];
  let spritePoolCreated = false;

  // Create sprite pool for transaction particles
  const createSpritePool = () => {
    if (spritePoolCreated) return;
    spritePoolCreated = true;

    for (let i = 0; i < MAX_ACTIVE_TXS; i++) {
      const sprite = new Sprite(txSpriteTexture);
      sprite.visible = false;
      sprite.tint = 0xffffff; // Default white, will be tinted per-transaction
      txSprites.push(sprite);
      txContainer.addChild(sprite);
    }
  };

  // ============================================================================
  // RESIZE HANDLING
  // ============================================================================

  let currentWorldWidth = worldWidth;
  let currentWorldHeight = worldHeight;
  let resizeCompleteCallback: ((worldWidth: number, worldHeight: number) => void) | null = null;

  const updateScale = (width: number = viewportWidth, height: number = viewportHeight) => {
    // No scaling - viewport handles everything at 1:1 scale
    app.stage.scale.set(1);
    app.stage.x = 0;
    app.stage.y = 0;
  };

  const handleResize = () => {
    const newWidth = containerElement.clientWidth;
    const newHeight = containerElement.clientHeight;

    // Update canvas size
    app.renderer.resize(newWidth, newHeight);

    // Update CSS to fit viewport (handles high DPI)
    canvas.style.width = `${newWidth}px`;
    canvas.style.height = `${newHeight}px`;

    // Calculate new world size - height adjusted for viewport scaling
    const newWorldWidth = SCENE_WIDTH;
    const newScale = newWidth / SCENE_WIDTH;

    // Apply same minimum circle area logic as initial setup
    const minCircleAreaWorld = MIN_CIRCLE_AREA_HEIGHT / newScale;
    const newViewportHeightWorld = newHeight / newScale;
    const newMinMaxY = CIRCLE_START_Y + minCircleAreaWorld;
    const newMaxY = Math.max(newViewportHeightWorld, newMinMaxY);
    const newWorldHeight = newMaxY; // World height is exactly where circles end

    // Update viewport with both screen AND world size
    viewport.resize(newWidth, newHeight, newWorldWidth, newWorldHeight);

    // Update scale to fit new screen width
    viewport.fitWidth(SCENE_WIDTH, false, true, false);

    // Update clampZoom to match new scale
    viewport.clampZoom({
      minScale: viewport.scale.x,
      maxScale: viewport.scale.x
    });

    // Update world size tracking
    currentWorldWidth = newWorldWidth;
    currentWorldHeight = newWorldHeight;

    // Update scroll bounds
    updateMaxScroll();

    // Notify resize complete callback (for CircleRenderer bounds update)
    if (resizeCompleteCallback) {
      resizeCompleteCallback(newWorldWidth, newWorldHeight);
    }
  };

  // Apply initial scale
  updateScale();

  // Handle window resize
  window.addEventListener('resize', handleResize);

  // ============================================================================
  // START ANIMATION LOOP
  // ============================================================================

  // Unlock FPS limit for 120+ FPS on high refresh rate monitors
  app.ticker.maxFPS = 0; // 0 = unlimited, runs at monitor refresh rate

  // Start animation loop
  // OPTIMIZATION: Skip viewport.y updates when scroll delta is tiny
  const SCROLL_THRESHOLD = 0.5; // Skip updates smaller than 0.5px
  const SCROLL_LERP_SPEED = 0.15; // Interpolation speed per 16.67ms frame
  app.ticker.add((ticker) => {
    // Smooth scroll interpolation (like Phaser camera lerp)
    // OPTIMIZATION: Delta-time aware interpolation - consistent speed at any FPS
    const deltaMs = ticker.deltaMS;
    const scrollLerpFactor = 1 - Math.pow(1 - SCROLL_LERP_SPEED, deltaMs / 16.67);
    const scrollDelta = (targetScrollY - currentScrollY) * scrollLerpFactor;
    if (Math.abs(scrollDelta) > SCROLL_THRESHOLD) {
      currentScrollY += scrollDelta;
      viewport.y = -currentScrollY;
    } else if (Math.abs(targetScrollY - currentScrollY) > 0.01) {
      // Snap to target when very close (avoid endless tiny updates)
      currentScrollY = targetScrollY;
      viewport.y = -currentScrollY;
    }

    // Call user update callback
    updateCallback(deltaMs);
  });

  // console.log('✅ Pixi.js initialized for data visualization');

  // ============================================================================
  // VISIBILITY-BASED TICKER CONTROL
  // ============================================================================

  // Stop the ticker completely when tab is hidden to save CPU/GPU
  const handleVisibilityChange = () => {
    if (document.hidden) {
      app.ticker.stop();
      // console.log('🎬 Ticker stopped (tab hidden)');
    } else {
      app.ticker.start();
      // console.log('🎬 Ticker resumed (tab visible)');
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // ============================================================================
  // CLEANUP FUNCTION
  // ============================================================================

  const cleanup = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('resize', handleResize);
    canvas.removeEventListener('wheel', handleWheel);
    canvas.removeEventListener('touchstart', handleTouchStart);
    canvas.removeEventListener('touchmove', handleTouchMove);
    unsubscribeTheme();
    if (viewport) {
      viewport.destroy();
    }
    if (app) {
      app.destroy(true);
    }
  };

  // ============================================================================
  // RETURN INSTANCE
  // ============================================================================

  return {
    app,
    viewport,
    containers: {
      blockchain: blockchainContainer,
      miniblock: miniblockContainer,
      tx: txContainer,
      circles: circlesContainer,
      lines: linesContainer,
      sequencer: sequencerContainer,
      barGraph: barGraphContainer,
      stats: statsContainer,
    },
    sprites: {
      txSprites,
      txTexture: txSpriteTexture,
    },
    viewportSize: {
      width: viewportWidth,
      height: viewportHeight,
    },
    worldSize: {
      width: currentWorldWidth,
      height: currentWorldHeight,
    },
    cleanup,
    resize: handleResize,
    updateScale,
    onResizeComplete: (callback: (worldWidth: number, worldHeight: number) => void) => {
      resizeCompleteCallback = callback;
    },
    createSpritePool,
  };
}
